/**
 * Servicio para gestionar eventos de Google Analytics
 * 
 * Este servicio proporciona una capa de abstracción para interactuar
 * con Google Analytics, permitiendo analizar y optimizar el embudo de conversión.
 */

import { GA_TRACKING_ID, GA_CONFIG } from './gaConfig';

// Definición de eventos de Google Analytics
export const GA_EVENTS = {
  // Eventos estándar
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  PAGE_VIEW: 'page_view',
  VIEW_ITEM: 'view_item',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_PAYMENT_INFO: 'add_payment_info',
  PURCHASE: 'purchase',
  
  // Eventos personalizados para la aplicación
  SCAN_INITIATED: 'scan_initiated',
  SCAN_COMPLETED: 'scan_completed',
  REPORT_VIEWED: 'report_viewed',
  PLAN_VIEWED: 'plan_viewed',
  LOADER_DISPLAYED: 'loader_displayed'
};

/**
 * Verifica si Google Analytics está disponible
 * @returns {boolean} - True si Google Analytics está disponible
 */
const isGaAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Rastrea un evento de Google Analytics
 * @param {string} eventName - Nombre del evento a rastrear
 * @param {Object} params - Parámetros adicionales para el evento
 */
export const trackGaEvent = (eventName, params = {}) => {
  if (!isGaAvailable()) {
    console.warn('[Google Analytics] gtag no está disponible');
    return false;
  }
  
  try {
    // Añadir el parámetro send_to
    const eventParams = {
      ...params,
      send_to: GA_TRACKING_ID
    };
    
    // Enviar el evento a Google Analytics
    window.gtag('event', eventName, eventParams);
    
    console.log(`[Google Analytics] Evento rastreado: ${eventName}`, eventParams);
    return true;
  } catch (error) {
    console.error(`[Google Analytics] Error al rastrear evento ${eventName}:`, error);
    return false;
  }
};

/**
 * Establece el ID de usuario en Google Analytics
 * @param {string} userId - ID del usuario
 */
export const setGaUserId = (userId) => {
  if (!isGaAvailable() || !userId) {
    return false;
  }
  
  try {
    window.gtag('config', GA_TRACKING_ID, {
      user_id: userId
    });
    
    console.log(`[Google Analytics] User ID configurado: ${userId}`);
    return true;
  } catch (error) {
    console.error('[Google Analytics] Error al configurar User ID:', error);
    return false;
  }
};

/**
 * Rastrea una vista de página
 * @param {Object} params - Parámetros adicionales
 */
export const trackPageView = (params = {}) => {
  if (!isGaAvailable()) {
    return false;
  }
  
  try {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: params.page_path || window.location.pathname,
      page_title: params.page_title || document.title,
      ...params
    });
    
    console.log(`[Google Analytics] Vista de página rastreada: ${params.page_path || window.location.pathname}`);
    return true;
  } catch (error) {
    console.error('[Google Analytics] Error al rastrear vista de página:', error);
    return false;
  }
};

/**
 * Rastrea un registro completado
 * @param {string} method - Método de registro (email, google, etc)
 */
export const trackSignUp = (method = 'website', params = {}) => {
  return trackGaEvent(GA_EVENTS.SIGN_UP, {
    method,
    ...params
  });
};

/**
 * Rastrea un inicio de sesión
 * @param {string} method - Método de inicio de sesión (email, google, etc)
 */
export const trackLogin = (method = 'website', params = {}) => {
  return trackGaEvent(GA_EVENTS.LOGIN, {
    method,
    ...params
  });
};

/**
 * Rastrea el inicio de un escaneo/análisis
 * @param {Object} params - Parámetros del análisis
 */
export const trackScanInitiated = (params = {}) => {
  return trackGaEvent(GA_EVENTS.SCAN_INITIATED, {
    content_type: params.content_type || 'basic',
    ...params
  });
};

/**
 * Rastrea un análisis completado
 * @param {Object} params - Parámetros del análisis
 */
export const trackScanCompleted = (params = {}) => {
  return trackGaEvent(GA_EVENTS.SCAN_COMPLETED, {
    content_type: params.content_type || 'basic',
    success: params.success !== undefined ? params.success : true,
    ...params
  });
};

/**
 * Rastrea cuando un usuario ve un reporte
 * @param {string} reportId - ID del reporte
 * @param {Object} params - Parámetros adicionales
 */
export const trackReportViewed = (reportId, params = {}) => {
  return trackGaEvent(GA_EVENTS.REPORT_VIEWED, {
    content_id: reportId,
    ...params
  });
};

/**
 * Rastrea cuando un usuario ve la página de planes
 * @param {string} source - Fuente desde donde llegó a la página de planes
 */
export const trackPlanViewed = (source = 'direct', params = {}) => {
  return trackGaEvent(GA_EVENTS.PLAN_VIEWED, {
    source,
    ...params
  });
};

/**
 * Rastrea cuando un usuario inicia el proceso de pago
 * @param {string} planId - ID del plan seleccionado
 * @param {number} value - Valor del plan
 * @param {string} currency - Moneda (por defecto MXN)
 */
export const trackBeginCheckout = (planId, value, currency = 'MXN', params = {}) => {
  return trackGaEvent(GA_EVENTS.BEGIN_CHECKOUT, {
    items: [{
      item_id: planId,
      item_name: `Plan: ${planId}`,
      price: value,
      quantity: 1
    }],
    value,
    currency,
    ...params
  });
};

/**
 * Rastrea cuando un usuario añade información de pago
 * @param {string} planId - ID del plan seleccionado
 * @param {number} value - Valor del plan
 * @param {string} paymentType - Tipo de pago (tarjeta, oxxo, etc)
 */
export const trackAddPaymentInfo = (planId, value, paymentType, currency = 'MXN', params = {}) => {
  return trackGaEvent(GA_EVENTS.ADD_PAYMENT_INFO, {
    items: [{
      item_id: planId,
      item_name: `Plan: ${planId}`,
      price: value,
      quantity: 1
    }],
    value,
    currency,
    payment_type: paymentType,
    ...params
  });
};

/**
 * Rastrea una compra completada
 * @param {string} planId - ID del plan comprado
 * @param {number} value - Valor del plan
 * @param {string} transactionId - ID de la transacción
 */
export const trackPurchase = (planId, value, transactionId, currency = 'MXN', params = {}) => {
  return trackGaEvent(GA_EVENTS.PURCHASE, {
    transaction_id: transactionId,
    items: [{
      item_id: planId,
      item_name: `Plan: ${planId}`,
      price: value,
      quantity: 1
    }],
    value,
    currency,
    ...params
  });
};

/**
 * Rastrea cuando un usuario ve el demo
 * @param {string} source - Fuente/ubicación del demo (landing, etc.)
 * @param {Object} params - Parámetros adicionales
 */
export const trackDemoViewed = (source, params = {}) => {
  trackGaEvent('demo_viewed', {
    source,
    ...params
  });
};

/**
 * Rastrea cuando un usuario selecciona un perro en el demo
 * @param {string} dogId - ID del perro seleccionado
 * @param {string} dogBreed - Raza del perro
 * @param {Object} params - Parámetros adicionales
 */
export const trackDemoDogSelected = (dogId, dogBreed, params = {}) => {
  trackGaEvent('demo_dog_selected', {
    dog_id: dogId,
    dog_breed: dogBreed,
    ...params
  });
};

/**
 * Rastrea cuando un usuario inicia un análisis de demo
 * @param {string} dogId - ID del perro
 * @param {string} dogBreed - Raza del perro
 * @param {Object} params - Parámetros adicionales
 */
export const trackDemoAnalysisStarted = (dogId, dogBreed, params = {}) => {
  trackGaEvent('demo_analysis_started', {
    dog_id: dogId,
    dog_breed: dogBreed,
    ...params
  });
};

/**
 * Rastrea cuando un análisis de demo se completa
 * @param {string} dogId - ID del perro
 * @param {string} dogBreed - Raza del perro
 * @param {number} healthScore - Puntuación de salud
 * @param {Object} params - Parámetros adicionales
 */
export const trackDemoAnalysisCompleted = (dogId, dogBreed, healthScore, params = {}) => {
  trackGaEvent('demo_analysis_completed', {
    dog_id: dogId,
    dog_breed: dogBreed,
    health_score: healthScore,
    ...params
  });
};

/**
 * Rastrea cuando un usuario va del demo al registro
 * @param {string} dogId - ID del perro del demo
 * @param {string} source - Fuente de conversión
 * @param {Object} params - Parámetros adicionales
 */
export const trackDemoToRegister = (dogId, source, params = {}) => {
  trackGaEvent('demo_to_register', {
    dog_id: dogId,
    source,
    ...params
  });
}; 